import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";

import { setWidgetHoverAtom } from "atoms/interaction";
import { EvaChart } from "constants/charts.enums";
import { EntityType } from "constants/entities.enum";
import { ChartStatus, useChartsContext } from "contexts/ChartContext";
import { UNDEFINED_LOCKS, UNDEFINED_MPC_LOCKS } from "entities/charts/chart";
import { createFeatureEntities } from "entities/charts/eva/features";
import { RIG_FRAC_ACTIVITY_LEGEND_X_BUFFER } from "entities/charts/eva/translators/rigFracActivity";
import { createEntityState } from "entities/charts/factory";
import { useSetAtom } from "jotai";
import { createChart, removeChart } from "store/features/charts/chartSlice";
import styled from "styled-components/macro";
import { noop } from "utils";

import { useChartEntities, useRigFracSession } from "hooks/charts";
import type { ChartSettings } from "hooks/charts";
import useChartSize from "hooks/charts/echart/useChartSize";

import { ResultMessageType } from "models/model";

import { ErrorBoundary } from "components/base";
import { AxisContainer } from "components/charts/components/AxisContainer/AxisContainer";
import Screenshot from "components/charts/components/Screenshot";
import { ChartLegend } from "components/charts/legend";
import { TimeScaleSelector } from "components/charts/toggles/timescale";
import Indicator from "components/ui/Indicator";

import RigFracBinSettingsToggle from "./RigFracBinSettingsToggle";
import RigFracChart from "./RigFracChart";
import RigFracChartToolbar from "./RigFracChartToolbar";
import RigFracDataTable from "./RigFracDataTable";
import RigFracMetricSelector from "./RigFracMetricSelector";

interface RigFracChartWrapperT {
  className?: string;
  onFullscreen?: (b: boolean) => void;
}

/**
 * Slim sibling of ChartWrapper for the Rig/Frac Charts widget (EVA-76 US1).
 * Owns the chart entity lifecycle (createChart/removeChart), the minimal
 * toolbar, legend, screenshot plumbing, and the left/right metric selectors.
 */
function RigFracChartWrapper({
  className = "",
  onFullscreen = noop
}: RigFracChartWrapperT) {
  const { id, status, initialChartSettings } = useChartsContext();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const setWidgetHover = useSetAtom(setWidgetHoverAtom);

  const screenshotContainerId = `screenshot-container-${id}`;

  const {
    fullScreen,
    screenshot,
    chartLegend,
    bounds,
    dataTable,
    apiResponse,
    chartType
  } = useChartEntities(id);

  const isFullscreenActive = fullScreen?.active;
  const isScreenshotActive = screenshot?.active;
  const isDataTableActive = dataTable?.active;
  // Activity Over Time carries the time-scale + dual (left/right) metric axes;
  // Recent Activity is a single-metric ranked bar chart (no time-scale).
  const isActivityOverTime = chartType === EvaChart.RigFracActivity;

  // Backend/gRPC failures come back as HTTP 200 with a GeneralError message —
  // surface them as a failure, NOT "No Data Available", so the user can tell an
  // outage from a genuinely empty area.
  const isServerError = apiResponse?.messageType === ResultMessageType.GeneralError;
  const isMissingData =
    !apiResponse ||
    (apiResponse.series && apiResponse.series.length === 0) ||
    apiResponse.messageType === ResultMessageType.NoData;

  // Shared floating legend (same component/style as the wells charts), fed by
  // the RigFracActivity branch in useChartLegendItems. Floats over the chart —
  // it does NOT reserve plot space.
  const showChartLegend =
    chartLegend?.active && !isScreenshotActive && !isDataTableActive && bounds?.width > 0;

  useChartSize(id, wrapperRef);
  useEffect(() => onFullscreen(isFullscreenActive), [isFullscreenActive]);

  // Persist the widget's selections to sessionStorage so a dashboard save/reload
  // restores them (read back via initialChartSettings on the mount seed below).
  useRigFracSession(id);

  // Initial chart creation upon first render (mirrors ChartWrapper). SEED from
  // initialChartSettings (the widget's componentState, restored from a saved
  // dashboard) so the user's chart type / features / locks / request selections
  // come back; fall back to Activity Over Time defaults on a fresh widget.
  useEffect(() => {
    // componentState carries an extra `request` (selections) beyond ChartSettings.
    const seed = initialChartSettings as unknown as Partial<ChartSettings> & {
      request?: GenericObject;
    };
    const seedChartType =
      seed?.chartType === EvaChart.RigFracRecentActivity
        ? EvaChart.RigFracRecentActivity
        : EvaChart.RigFracActivity;

    dispatch(
      createChart({
        id,
        ...createEntityState(EntityType.Chart, {
          id,
          chartType: seedChartType,
          features: createFeatureEntities(seedChartType, seed?.features),
          locks: seed?.locks ?? UNDEFINED_LOCKS,
          mpcLocks: seed?.mpcLocks ?? UNDEFINED_MPC_LOCKS,
          // Seed the persisted selections; the controller rebuilds the full
          // request (adding the live map bbox) from them. undefined on a fresh
          // widget → controller uses the per-chart defaults.
          request: seed?.request as never,
          // Valid JSON from the start: Screenshot JSON.parses entity options.
          options: "{}"
        })
      })
    );

    return () => {
      dispatch(removeChart(id));
    };
    // Seed once on mount; initialChartSettings is stable per widget mount and
    // must NOT be a dep (a new identity would remove+recreate the entity, wiping
    // live state). createChart is a no-op if the entity already exists.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, dispatch]);

  // Hover registration drives toolbar/selector visibility (same widgetHoverAtom
  // key every chart hook reads as isHover — mirrors ChartWrapper/PopoutChart).
  const handleMouseEnter = () => {
    setWidgetHover({ widgetId: id, isHover: true });
  };
  const handleMouseLeave = () => {
    setWidgetHover({ widgetId: id, isHover: false });
  };

  return (
    <ErrorBoundary>
      <Wrapper
        ref={wrapperRef} // for useChartSize
        className={className}
        data-testid="rigFracChartWrapper"
        $screenshot={isScreenshotActive}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}>
        <ChartContainer $screenshot={isScreenshotActive}>
          <RigFracChart />
        </ChartContainer>

        {/* Data table overlays the chart when toggled on (export lives inside it). */}
        {isDataTableActive && <RigFracDataTable />}

        {status === ChartStatus.Idle &&
          (isMissingData || isServerError) &&
          !isDataTableActive && (
            <Indicator
              message={isServerError ? "Failed to Load Chart Data" : "No Data Available"}
            />
          )}

        <RigFracChartToolbar />

        {showChartLegend && (
          <ChartLegend
            parentDimensions={{ width: bounds.width, height: bounds.height }}
            id={screenshotContainerId + "chart-legend"}
            screenshot={screenshot}
            bounds={bounds}
            legend={chartLegend}
            // Activity's right-axis dataZoom slider hugs the right plot edge,
            // exactly under the legend's default NE spot — and the legend is a
            // DOM overlay the canvas handle can't beat. Push this chart's
            // DEFAULT legend position clear of the slider band (drag still
            // goes anywhere). Recent Activity has no y sliders — default spot.
            xPositionBuffer={
              isActivityOverTime ? RIG_FRAC_ACTIVITY_LEGEND_X_BUFFER : undefined
            }
          />
        )}

        {!isDataTableActive &&
          (isActivityOverTime ? (
            <>
              {/* Left = bars metric, right = overlay-line metric; both rotated on
                  their y-axes. Time-scale bar is Activity-only. */}
              <RigFracMetricSelector side="left" />
              <RigFracMetricSelector side="right" />
              <TimeScaleSelector />
            </>
          ) : (
            // Recent Activity's single metric maps to the bottom value axis.
            <RigFracMetricSelector side="bottom" />
          ))}
        {!isDataTableActive && <RigFracBinSettingsToggle />}

        {/* Y-axis lock handles (#6) — Activity Over Time only; self-gates on
            hover/screenshot/data-table and reads the lock config from the
            persisted options JSON (dispatched by RigFracChart). */}
        {isActivityOverTime && !isDataTableActive && <AxisContainer />}

        <Screenshot containerId={screenshotContainerId} />
      </Wrapper>
    </ErrorBoundary>
  );
}

export default RigFracChartWrapper;

const Wrapper = styled.div<{ $screenshot?: boolean }>`
  width: 100%;
  height: 100%;
  min-width: 0;
  display: grid;
  grid-template-rows: minmax(0, 1fr);
  border-radius: var(--border-radius);
  box-shadow: rgba(var(--color-shadow-rgb), 0.2) 0 0 1px;
  padding-top: 10px;
  z-index: 0;
  position: relative;
  /* Grey backdrop behind the centered chart card while screenshotting — matches
     the shared wells ChartWrapper (rgba(46,72,88,0.24)). */
  background-color: ${(props) =>
    props.$screenshot ? "rgba(46, 72, 88, 0.24)" : "transparent"};

  &.fullscreen {
    width: 100vw;
    height: 100vh;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: var(--index-fullscreen);
  }
`;

const ChartContainer = styled.div<{ $screenshot?: boolean }>`
  min-height: 0;
  /* While screenshotting, center the fixed preset-size chart card within the
     grey backdrop (mirrors the wells ChartContainer justify-self: center);
     overflow keeps an oversized preset scrollable rather than clipping. */
  ${(props) =>
    props.$screenshot
      ? `
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: auto;
  `
      : ""}

  &:hover {
    z-index: 1;
  }
`;
